import { useState, useEffect } from "react";
import { fetchWithTimeout } from "../../App";
import "../../App.css";
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL;

const CATEGORIES = [
  { value: "ingredients", label: "🧂 Ingredients" },
  { value: "packaging", label: "📦 Packaging" },
  { value: "equipment", label: "🔧 Equipment" },
  { value: "recipe_cost", label: "🍳 Recipe / Cooking Cost" },
  { value: "other", label: "📌 Other" },
];

function Reports() {
  const [summary, setSummary] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");

  const [allOrders, setAllOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [locations, setLocations] = useState([]);

  const [feedback, setFeedback] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");

  const [expDesc, setExpDesc] = useState("");
  const [expAmount, setExpAmount] = useState("");
  const [expCategory, setExpCategory] = useState("ingredients");
  const [expError, setExpError] = useState("");
  const [expSuccess, setExpSuccess] = useState("");

  const [loyaltyOffers, setLoyaltyOffers] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [loyaltyLoaded, setLoyaltyLoaded] = useState(false);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltyError, setLoyaltyError] = useState("");
  const [offerForm, setOfferForm] = useState({
    name: "",
    description: "",
    min_points: "0",
    discount_amount: "",
    active: true,
  });
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [offerSuccess, setOfferSuccess] = useState("");

  const loadLoyaltyData = async () => {
    setLoyaltyLoading(true);
    setLoyaltyError("");
    try {
      const [offersRes, customersRes] = await Promise.all([
        fetchWithTimeout(`${API_URL}/api/admin/loyalty/offers`, { credentials: "include" }),
        fetchWithTimeout(`${API_URL}/api/admin/loyalty/customers?limit=10`, { credentials: "include" }),
      ]);

      if (!offersRes.ok || !customersRes.ok) {
        const offersText = !offersRes.ok ? await offersRes.text() : "";
        const customersText = !customersRes.ok ? await customersRes.text() : "";
        throw new Error(
          [
            !offersRes.ok ? `offers ${offersRes.status}` : "",
            !customersRes.ok ? `customers ${customersRes.status}` : "",
            offersText || customersText ? "non-json response" : "",
          ]
            .filter(Boolean)
            .join(", ")
        );
      }

      const offersData = await offersRes.json();
      const customersData = await customersRes.json();

      setLoyaltyOffers(Array.isArray(offersData) ? offersData : []);
      setTopCustomers(Array.isArray(customersData) ? customersData : []);
      setLoyaltyLoaded(true);
    } catch (err) {
      console.error(err);
      setLoyaltyError("Could not load loyalty data.");
    }
    setLoyaltyLoading(false);
    setLoyaltyLoaded(true);
  };

  const resetOfferForm = () => {
    setOfferForm({
      name: "",
      description: "",
      min_points: "0",
      discount_amount: "",
      active: true,
    });
    setEditingOfferId(null);
  };

  const handleOfferSubmit = async (e) => {
    e.preventDefault();
    setLoyaltyError("");
    setOfferSuccess("");

    if (!offerForm.name.trim()) {
      setLoyaltyError("Offer name is required.");
      return;
    }
    if (!offerForm.discount_amount || Number(offerForm.discount_amount) <= 0) {
      setLoyaltyError("Discount amount must be greater than 0.");
      return;
    }

    const payload = {
      name: offerForm.name.trim(),
      description: offerForm.description.trim(),
      min_points: Number(offerForm.min_points) || 0,
      discount_amount: Number(offerForm.discount_amount),
      active: Boolean(offerForm.active),
    };

    const url = editingOfferId
      ? `${API_URL}/api/admin/loyalty/offers/${editingOfferId}`
      : `${API_URL}/api/admin/loyalty/offers`;

    const res = await fetch(url, {
      method: editingOfferId ? "PATCH" : "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    const data = await res.json();

    if (!res.ok) {
      setLoyaltyError(data.error || "Could not save offer.");
      return;
    }

    setOfferSuccess(editingOfferId ? "Offer updated." : "Offer created.");
    resetOfferForm();
    await loadLoyaltyData();
    setTimeout(() => setOfferSuccess(""), 2500);
  };

  const handleOfferEdit = (offer) => {
    setEditingOfferId(offer.id);
    setOfferForm({
      name: offer.name || "",
      description: offer.description || "",
      min_points: String(offer.min_points ?? 0),
      discount_amount: String(offer.discount_amount ?? ""),
      active: Boolean(offer.active),
    });
    setTab("loyalty");
  };

  const handleOfferDelete = async (id) => {
    if (!window.confirm("Delete this loyalty offer?")) return;
    await fetch(`${API_URL}/api/admin/loyalty/offers/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    await loadLoyaltyData();
  };

  const handleOfferToggle = async (offer) => {
    const res = await fetch(`${API_URL}/api/admin/loyalty/offers/${offer.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        name: offer.name,
        description: offer.description || "",
        min_points: offer.min_points,
        discount_amount: offer.discount_amount,
        active: !offer.active,
      }),
    });

    if (!res.ok) return;
    await loadLoyaltyData();
  };

  const fetchSummary = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/reports/summary`, { credentials: "include" });
      const data = await res.json();
      if (Array.isArray(data)) setSummary(data);
    } catch (err) { console.error(err); }
  };

  const fetchAllOrders = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/orders`, { credentials: "include" });
      const data = await res.json();
      if (Array.isArray(data)) setAllOrders(data);
    } catch (err) { console.error(err); }
  };

  const fetchMenu = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/menu`);
      const data = await res.json();
      if (Array.isArray(data)) setMenu(data);
    } catch (err) { console.error(err); }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetchWithTimeout(`${API_URL}/locations`);
      const data = await res.json();
      if (Array.isArray(data)) setLocations(data);
    } catch (err) { console.error(err); }
  };

  const fetchReport = async (sid) => {
    if (!sid) return;
    setLoading(true);
    try {
      const res = await fetchWithTimeout(`${API_URL}/api/admin/reports/session/${sid}`, { credentials: "include" });
      const data = await res.json();
      setReport(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchFeedback = async () => {
    setFeedbackLoading(true);
    setFeedbackError("");
    try {
      const CSV_URL = process.env.REACT_APP_FEEDBACK_CSV_URL;
      if (!CSV_URL) { setFeedbackError("Feedback CSV URL not configured."); setFeedbackLoading(false); return; }

      const res = await fetchWithTimeout(`${API_URL}/api/admin/feedback-csv`, { credentials: "include" }, 10000);
      if (!res.ok) throw new Error("Failed to fetch");
      const text = await res.text();

      const lines = text.trim().split("\n");
      const rows = lines.slice(1).map(line => {
        const cols = [];
        let cur = "", inQuote = false;
        for (let i = 0; i < line.length; i++) {
          if (line[i] === '"') { inQuote = !inQuote; }
          else if (line[i] === "," && !inQuote) { cols.push(cur.trim()); cur = ""; }
          else { cur += line[i]; }
        }
        cols.push(cur.trim());
        return {
          timestamp:     cols[0]  || "",
          times_ordered: cols[1]  || "",
          overall:       parseInt(cols[2]) || 0,
          vs_previous:   cols[3]  || "",
          disappointed:  cols[4]  || "",
          taste:         parseInt(cols[5]) || 0,
          portion:       parseInt(cols[6]) || 0,
          value:         parseInt(cols[7]) || 0,
          consistency:   parseInt(cols[8]) || 0,
          why_no_order:  cols[9]  || "",
          price_feeling: cols[10] || "",
          reorder:       cols[11] || "",
          improve_one:   cols[12] || "",
          comments:      cols[13] || "",
        };
      }).filter(r => r.overall > 0);

      setFeedback(rows);
    } catch (err) {
      console.error(err);
      setFeedbackError("Could not load feedback data. Check your CSV URL.");
    }
    setFeedbackLoading(false);
  };

  useEffect(() => { Promise.allSettled([fetchSummary(), fetchAllOrders(), fetchMenu(), fetchLocations()]); }, []);
  useEffect(() => { if (selectedSession) fetchReport(selectedSession); }, [selectedSession]);
  useEffect(() => {
    if (tab === "loyalty" && !loyaltyLoaded && !loyaltyLoading) {
      loadLoyaltyData();
    }
  }, [tab, loyaltyLoaded, loyaltyLoading]);

  const calcProfit = (revenue, expenses, recipeCostTotal) => {
    return revenue - (recipeCostTotal || 0) - expenses;
  };
  const calcMargin = (revenue, profit) => {
    if (!revenue || revenue === 0) return 0;
    return parseFloat(((profit / revenue) * 100).toFixed(1));
  };

  const addExpense = async (e) => {
    e.preventDefault();
    setExpError(""); setExpSuccess("");
    if (!expDesc.trim()) return setExpError("Description is required.");
    if (!expAmount || parseFloat(expAmount) <= 0) return setExpError("Amount must be greater than 0.");

    const res = await fetch(`${API_URL}/api/admin/expenses`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        session_id: selectedSession || null,
        description: expDesc,
        amount: parseFloat(expAmount),
        category: expCategory,
      }),
    });
    const data = await res.json();
    if (!res.ok) return setExpError(data.error);

    setExpDesc(""); setExpAmount(""); setExpCategory("ingredients");
    setExpSuccess("Expense added!");
    if (selectedSession) fetchReport(selectedSession);
    fetchSummary();
    setTimeout(() => setExpSuccess(""), 3000);
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await fetch(`${API_URL}/api/admin/expenses/${id}`, { method: "DELETE", credentials: "include" });
    if (selectedSession) fetchReport(selectedSession);
    fetchSummary();
  };

  const exportExcel = () => {
    if (!report) return;
    const hasRecipes = report.recipe_costs?.items?.some(i => i.has_recipe);
    const recipeCostTotal = report.recipe_costs?.total || 0;
    const profit = calcProfit(report.revenue.total, report.expenses.total, hasRecipes ? recipeCostTotal : 0);
    const margin = calcMargin(report.revenue.total, profit);

    const wb = XLSX.utils.book_new();

    // ===== Sheet 1: Summary =====
    const summaryData = [
      ["Session Report"],
      ["Period", `${fmt(report.session.open_time)} → ${fmt(report.session.close_time)}`],
      [],
      ["REVENUE"],
      ["Total Orders", report.revenue.total_orders],
      ["Total Items Sold", report.revenue.total_items],
      ["Total Revenue (RM)", report.revenue.total],
      [],
      ...(hasRecipes ? [
        ["Recipe Cost (RM)", recipeCostTotal],
      ] : []),
      ["Other Expenses (RM)", report.expenses.total],
      [],
      ["PROFIT SUMMARY"],
      ["Net Profit (RM)", profit],
      ["Profit Margin (%)", margin],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    const summaryWidths = [{ wch: 25 }, { wch: 15 }];
    wsSummary['!cols'] = summaryWidths;
    // Bold header row
    const summaryRange = XLSX.utils.decode_range(wsSummary['!ref']);
    for (let C = 0; C <= summaryRange.e.c; ++C) {
      const cellAddr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (wsSummary[cellAddr]) wsSummary[cellAddr].s = { font: { bold: true } };
    }
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    // ===== Sheet 2: Orders =====
    const ordersData = [
      ["Name", "Phone", "Item", "Qty", "Location", "Item Amount (RM)", "Delivery Fee (RM)", "Total (RM)", "Status", "Payment Status"]
    ];
    report.revenue.orders.forEach(o => {
      const itemAmt = parseFloat(o.item_price || 0) * parseInt(o.quantity);
      const delFee = parseFloat(o.delivery_fee || 0);
      ordersData.push([
        o.name,
        o.phone,
        o.item,
        o.quantity,
        o.location,
        itemAmt,
        delFee,
        itemAmt + delFee,
        o.status,
        o.payment_status
      ]);
    });
    const wsOrders = XLSX.utils.aoa_to_sheet(ordersData);
    wsOrders['!cols'] = [
      { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 6 },
      { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
      { wch: 12 }, { wch: 15 }
    ];
    // Bold header row
    for (let C = 0; C <= 9; ++C) {
      const cellAddr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (wsOrders[cellAddr]) wsOrders[cellAddr].s = { font: { bold: true } };
    }
    XLSX.utils.book_append_sheet(wb, wsOrders, "Orders");

    // ===== Sheet 3: Item Breakdown (only if recipes exist) =====
    if (hasRecipes && report.recipe_costs?.items?.length > 0) {
      const itemBreakdownData = [
        ["Item", "Qty Sold", "Sell Price (RM)", "Cost/Portion (RM)", "Profit/Portion (RM)", "Total Revenue (RM)", "Total Cost (RM)", "Total Profit (RM)", "Margin (%)"]
      ];
      report.recipe_costs.items.forEach(i => {
        itemBreakdownData.push([
          i.item,
          i.qty_sold,
          i.selling_price || 0,
          i.has_recipe ? (i.cost_per_portion || 0) : null,
          i.has_recipe ? (i.profit_per_portion || 0) : null,
          i.total_revenue || 0,
          i.has_recipe ? (i.total_cost || 0) : null,
          i.has_recipe ? (i.total_profit || 0) : null,
          i.has_recipe ? (i.margin || 0) : null,
        ]);
      });
      const wsItemBreakdown = XLSX.utils.aoa_to_sheet(itemBreakdownData);
      wsItemBreakdown['!cols'] = [
        { wch: 25 }, { wch: 10 }, { wch: 15 }, { wch: 18 },
        { wch: 18 }, { wch: 18 }, { wch: 15 }, { wch: 18 }, { wch: 10 }
      ];
      // Bold header row
      for (let C = 0; C <= 8; ++C) {
        const cellAddr = XLSX.utils.encode_cell({ r: 0, c: C });
        if (wsItemBreakdown[cellAddr]) wsItemBreakdown[cellAddr].s = { font: { bold: true } };
      }
      XLSX.utils.book_append_sheet(wb, wsItemBreakdown, "Item Breakdown");
    }

    // ===== Sheet 4: Expenses =====
    const expensesData = [
      ["Description", "Category", "Amount (RM)"]
    ];
    report.expenses.items.forEach(e => {
      expensesData.push([
        e.description,
        CATEGORIES.find(c => c.value === e.category)?.label || e.category,
        parseFloat(e.amount)
      ]);
    });
    expensesData.push(["Total Other Expenses", "", report.expenses.total]);
    const wsExpenses = XLSX.utils.aoa_to_sheet(expensesData);
    wsExpenses['!cols'] = [{ wch: 40 }, { wch: 25 }, { wch: 15 }];
    // Bold header row
    for (let C = 0; C <= 2; ++C) {
      const cellAddr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (wsExpenses[cellAddr]) wsExpenses[cellAddr].s = { font: { bold: true } };
    }
    XLSX.utils.book_append_sheet(wb, wsExpenses, "Expenses");

    // Generate filename with session ID and date
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `session_report_${selectedSession}_${dateStr}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const fmt = (dt) => new Date(dt).toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short" });
  const fmtRM = (n) => `RM ${parseFloat(n || 0).toFixed(2)}`;

  const priceMap = Object.fromEntries(menu.map((m) => [m.name, parseFloat(m.price)]));
  const deliveryMap = Object.fromEntries(locations.map((l) => [l.name, parseFloat(l.delivery_fee)]));

  // Group by order_token so delivery fee is counted once per order, not per item row
  const overallRevenue = Object.values(
    allOrders.reduce((acc, row) => {
      const token = row.order_token || `solo-${row.id}`;
      if (!acc[token]) acc[token] = { location: row.location, rows: [] };
      acc[token].rows.push(row);
      return acc;
    }, {})
  ).reduce((sum, g) => {
    const itemsTotal = g.rows.reduce((s, r) => {
      const price = r.item_price != null && parseFloat(r.item_price) > 0
        ? parseFloat(r.item_price)
        : (priceMap[r.item] || 0);
      return s + price * r.quantity;
    }, 0);
    const deliveryFee = g.rows[0]?.delivery_fee != null
      ? parseFloat(g.rows[0].delivery_fee)
      : (deliveryMap[g.location] || 0);
    const discountTotal = g.rows.reduce((s, r) => s + parseFloat(r.discount_amount || 0), 0);
    return sum + itemsTotal + deliveryFee - discountTotal;
  }, 0);
  const overallExpenses = summary.reduce((s, r) => s + parseFloat(r.total_expenses || 0), 0);
  const overallRecipeCost = summary.reduce((s, r) => s + parseFloat(r.total_recipe_cost || 0), 0);
  const overallProfit = overallRevenue - overallRecipeCost - overallExpenses;
  const overallMargin = calcMargin(overallRevenue, overallProfit);

  return (
    <div style={{ maxWidth: "960px" }}>
      <h1 className="admin-page-heading">📈 Reports</h1>
      <p className="admin-page-sub">Revenue, expenses, and customer feedback</p>

      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
        <button onClick={() => setTab("overview")} style={tab === "overview" ? activeTabStyle : tabStyle}>
          📊 Overview
        </button>
        <button onClick={() => setTab("session")} style={tab === "session" ? activeTabStyle : tabStyle}>
          🔍 Session Detail
        </button>
        <button onClick={() => setTab("expenses")} style={tab === "expenses" ? activeTabStyle : tabStyle}>
          💸 Log Expense
        </button>
        <button onClick={() => setTab("loyalty")} style={tab === "loyalty" ? activeTabStyle : tabStyle}>
          🎁 Loyalty
        </button>
        <button onClick={() => { setTab("feedback"); if (feedback.length === 0) fetchFeedback(); }} style={tab === "feedback" ? activeTabStyle : tabStyle}>
          ⭐ Customer Satisfaction
        </button>
      </div>

      {tab === "overview" && (
        <>
          <div style={{ display: "flex", gap: "1rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
            <StatCard label="Total Revenue" value={fmtRM(overallRevenue)} color="var(--success)" />
            {overallRecipeCost > 0 && <StatCard label="Recipe Cost" value={fmtRM(overallRecipeCost)} color="var(--error)" />}
            <StatCard label="Other Expenses" value={fmtRM(overallExpenses)} color="var(--gold)" />
            <StatCard label="Net Profit" value={fmtRM(overallProfit)} color={overallProfit >= 0 ? "var(--orange)" : "var(--error)"} />
            <StatCard label="Margin" value={`${overallMargin}%`} color="var(--brown-mid)" />
          </div>

          <div style={cardStyle}>
            <h2 style={sectionHeading}>All Sessions</h2>
            {summary.length === 0 ? (
              <p style={{ color: "var(--text-muted)" }}>No sessions yet.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr style={{ background: "var(--beige)", borderBottom: "2px solid var(--border)" }}>
                      <th style={thStyle}>Session</th>
                      <th style={thStyle}>Orders</th>
                      <th style={thStyle}>Items</th>
                      <th style={thStyle}>Revenue</th>
                      <th style={thStyle}>Recipe Cost</th>
                      <th style={thStyle}>Expenses</th>
                      <th style={thStyle}>Profit</th>
                      <th style={thStyle}>Margin</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.map(s => {
                      const rev = parseFloat(s.total_revenue || 0);
                      const exp = parseFloat(s.total_expenses || 0);
                      const recipeCost = parseFloat(s.total_recipe_cost || 0);
                      const profit = s.profit !== undefined ? parseFloat(s.profit) : (rev - recipeCost - exp);
                      const margin = s.margin !== undefined ? parseFloat(s.margin) : calcMargin(rev, profit);
                      return (
                        <tr key={s.id} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={tdStyle}><span style={{ fontSize: "0.85rem" }}>{fmt(s.open_time)}</span></td>
                          <td style={tdStyle}>{s.total_orders}</td>
                          <td style={tdStyle}>{s.total_items}</td>
                          <td style={{ ...tdStyle, color: "var(--success)", fontWeight: "bold" }}>{fmtRM(rev)}</td>
                          <td style={{ ...tdStyle, color: "var(--error)" }}>{recipeCost > 0 ? fmtRM(recipeCost) : <span style={{ color: "var(--border)" }}>—</span>}</td>
                          <td style={{ ...tdStyle, color: "var(--gold)" }}>{fmtRM(exp)}</td>
                          <td style={{ ...tdStyle, fontWeight: "bold", color: profit >= 0 ? "var(--orange)" : "var(--error)" }}>{fmtRM(profit)}</td>
                          <td style={tdStyle}><MarginBadge margin={margin} /></td>
                          <td style={tdStyle}>
                            <button
                              onClick={() => { setSelectedSession(String(s.id)); setTab("session"); }}
                              style={viewBtnStyle}
                            >
                              View →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === "session" && (
        <>
          <div style={{ marginBottom: "1.5rem" }}>
            <label style={labelStyle}>Select Session</label>
            <select
              value={selectedSession}
              onChange={e => setSelectedSession(e.target.value)}
              style={{ ...inputStyle, maxWidth: "420px" }}
            >
              <option value="">Choose a session...</option>
              {summary.map(s => (
                <option key={s.id} value={s.id}>
                  {fmt(s.open_time)} — {fmtRM(s.total_revenue)} revenue
                </option>
              ))}
            </select>
          </div>

          {loading && <p style={{ color: "var(--text-muted)" }}>Loading...</p>}

          {report && !loading && (() => {
            const hasRecipes = report.recipe_costs?.items?.some(i => i.has_recipe);
            const recipeCostTotal = report.recipe_costs?.total || 0;
            const profit = calcProfit(report.revenue.total, report.expenses.total, hasRecipes ? recipeCostTotal : 0);
            const margin = calcMargin(report.revenue.total, profit);

            return (
              <>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
                  <button onClick={exportExcel} style={exportBtnStyle}>⬇️ Export Excel</button>
                </div>

                <div style={{ ...cardStyle, background: "var(--beige)" }}>
                  <h2 style={{ marginTop: 0, fontSize: "0.9rem", color: "var(--brown-mid)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Profit Summary
                  </h2>

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1.25rem", maxWidth: "400px" }}>
                    <CalcRow label="Revenue" value={fmtRM(report.revenue.total)} color="var(--success)" sign="+" />
                    {hasRecipes && (
                      <CalcRow label="Recipe Cost" value={`− ${fmtRM(recipeCostTotal)}`} color="var(--error)" sign="" />
                    )}
                    <CalcRow label="Other Expenses" value={`− ${fmtRM(report.expenses.total)}`} color="var(--gold)" sign="" />
                    <div style={{ borderTop: "2px solid var(--border)", paddingTop: "0.6rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: "bold", fontSize: "1rem", color: "var(--text-main)" }}>Net Profit</span>
                      <span style={{ fontWeight: "bold", fontSize: "1.3rem", color: profit >= 0 ? "var(--orange)" : "var(--error)", fontFamily: "'Playfair Display', Georgia, serif" }}>
                        {fmtRM(profit)}
                        <span style={{ fontSize: "0.9rem", marginLeft: "0.4rem" }}>({margin}%)</span>
                      </span>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                    <StatCard label="Revenue" value={fmtRM(report.revenue.total)} color="var(--success)" />
                    {hasRecipes && <StatCard label="Recipe Cost" value={fmtRM(recipeCostTotal)} color="var(--error)" />}
                    <StatCard label="Other Expenses" value={fmtRM(report.expenses.total)} color="var(--gold)" />
                    <StatCard label="Net Profit" value={fmtRM(profit)} color={profit >= 0 ? "var(--orange)" : "var(--error)"} />
                    <StatCard label="Margin" value={`${margin}%`} color="var(--brown-mid)" />
                  </div>

                  {!hasRecipes && (
                    <div style={{ ...noteBoxStyle, marginTop: "1rem", marginBottom: 0 }}>
                      ⚠️ No recipes linked yet — recipe costs not included. Go to <strong>Recipes</strong> and link each recipe to its menu item.
                    </div>
                  )}
                </div>

                {report.recipe_costs?.items?.length > 0 && (
                  <div style={cardStyle}>
                    <h2 style={sectionHeading}>🍳 Per-Item Profit Breakdown</h2>
                    <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginTop: 0, marginBottom: "1rem" }}>
                      Profit per portion = selling price − recipe cost per portion
                    </p>
                    <div style={{ overflowX: "auto" }}>
                      <table style={tableStyle}>
                        <thead>
                          <tr style={{ background: "var(--beige)", borderBottom: "2px solid var(--border)" }}>
                            <th style={thStyle}>Item</th>
                            <th style={thStyle}>Sold</th>
                            <th style={thStyle}>Sell Price</th>
                            <th style={thStyle}>Cost/Portion</th>
                            <th style={thStyle}>Profit/Portion</th>
                            <th style={thStyle}>Total Revenue</th>
                            <th style={thStyle}>Total Cost</th>
                            <th style={thStyle}>Total Profit</th>
                            <th style={thStyle}>Margin</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.recipe_costs.items.map(item => (
                            <tr key={item.item} style={{ borderBottom: "1px solid var(--border)" }}>
                              <td style={tdStyle}>
                                <strong>{item.item}</strong>
                                {item.recipe_name && (
                                  <span style={{ display: "block", fontSize: "0.75rem", color: "var(--text-muted)" }}>
                                    {item.recipe_name}
                                  </span>
                                )}
                                {!item.has_recipe && (
                                  <span style={{ display: "block", fontSize: "0.75rem", color: "var(--gold)" }}>
                                    ⚠️ No recipe linked
                                  </span>
                                )}
                              </td>
                              <td style={tdStyle}>{item.qty_sold}</td>
                              <td style={tdStyle}>{fmtRM(item.selling_price)}</td>
                              <td style={{ ...tdStyle, color: "var(--error)" }}>
                                {item.has_recipe ? fmtRM(item.cost_per_portion) : <Dash />}
                              </td>
                              <td style={{ ...tdStyle, fontWeight: "bold", color: (item.profit_per_portion || 0) >= 0 ? "var(--success)" : "var(--error)" }}>
                                {item.has_recipe ? fmtRM(item.profit_per_portion) : <Dash />}
                              </td>
                              <td style={{ ...tdStyle, color: "var(--success)" }}>{fmtRM(item.total_revenue)}</td>
                              <td style={{ ...tdStyle, color: "var(--error)" }}>
                                {item.has_recipe ? fmtRM(item.total_cost) : <Dash />}
                              </td>
                              <td style={{ ...tdStyle, fontWeight: "bold", color: (item.total_profit || 0) >= 0 ? "var(--orange)" : "var(--error)" }}>
                                {item.has_recipe ? fmtRM(item.total_profit) : <Dash />}
                              </td>
                              <td style={tdStyle}>
                                {item.has_recipe ? <MarginBadge margin={item.margin} /> : <Dash />}
                              </td>
                            </tr>
                          ))}
                          <tr style={{ background: "var(--beige)", borderTop: "2px solid var(--border)" }}>
                            <td style={{ ...tdStyle, fontWeight: "bold" }} colSpan={5}>Total</td>
                            <td style={{ ...tdStyle, color: "var(--success)", fontWeight: "bold" }}>{fmtRM(report.revenue.total)}</td>
                            <td style={{ ...tdStyle, color: "var(--error)", fontWeight: "bold" }}>{fmtRM(recipeCostTotal)}</td>
                            <td style={{ ...tdStyle, fontWeight: "bold", color: profit >= 0 ? "var(--orange)" : "var(--error)" }}>{fmtRM(profit)}</td>
                            <td style={tdStyle}><MarginBadge margin={margin} /></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {report.recipe_costs.items.some(i => !i.has_recipe) && (
                      <p style={{ fontSize: "0.82rem", color: "var(--gold)", marginTop: "0.75rem", marginBottom: 0 }}>
                        ⚠️ Items showing — have no linked recipe and are excluded from cost calculations.
                      </p>
                    )}
                  </div>
                )}

                {/* ── Delivery View: grouped by location ── */}
                {(() => {
                  // Group the flat order rows by location, then by order_token within each location
                  const byLocation = {};
                  report.revenue.orders.forEach(o => {
                    const loc = o.location || "Unknown";
                    if (!byLocation[loc]) byLocation[loc] = {};
                    const token = o.order_token || `solo-${o.id}`;
                    if (!byLocation[loc][token]) {
                      byLocation[loc][token] = {
                        token,
                        name: o.name,
                        phone: o.phone,
                        status: o.status,
                        payment_status: o.payment_status,
                        rows: [],
                      };
                    }
                    byLocation[loc][token].rows.push(o);
                  });

                  const locationGroups = Object.entries(byLocation).sort(([a], [b]) => a.localeCompare(b));
                  if (locationGroups.length === 0) return null;

                  return (
                    <div style={cardStyle}>
                      <h2 style={{ ...sectionHeading, marginBottom: "0.25rem" }}>🛵 Delivery View</h2>
                      <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginTop: 0, marginBottom: "1.25rem" }}>
                        Orders grouped by location — use this to plan the delivery run.
                      </p>
                      <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                        {locationGroups.map(([location, ordersMap]) => {
                          const orders = Object.values(ordersMap);
                          const totalQty = orders.reduce((s, g) => s + g.rows.reduce((ss, r) => ss + parseInt(r.quantity), 0), 0);
                          return (
                            <div key={location} style={{ border: "1px solid var(--border)", borderRadius: "12px", overflow: "hidden" }}>
                              {/* Location header */}
                              <div style={{
                                background: "var(--beige)",
                                padding: "0.65rem 1rem",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderBottom: "1px solid var(--border)",
                              }}>
                                <span style={{ fontWeight: "700", fontSize: "0.95rem", color: "var(--brown-mid)" }}>
                                  📍 {location}
                                </span>
                                <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                                  {orders.length} customer{orders.length !== 1 ? "s" : ""} · {totalQty} item{totalQty !== 1 ? "s" : ""}
                                </span>
                              </div>
                              {/* Orders at this location */}
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                {orders.map((group, idx) => (
                                  <div key={group.token} style={{
                                    padding: "0.65rem 1rem",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: "1rem",
                                    borderBottom: idx < orders.length - 1 ? "1px solid var(--border)" : "none",
                                    background: "var(--warm-white)",
                                  }}>
                                    <div>
                                      <span style={{ fontWeight: "600", fontSize: "0.9rem", color: "var(--text-main)" }}>
                                        {group.name}
                                      </span>
                                      {group.phone && (
                                        <span style={{ marginLeft: "0.6rem", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                                          {group.phone.startsWith("+") ? (
                                            <a href={`https://wa.me/${group.phone.replace("+", "")}`} target="_blank" rel="noreferrer" style={{ color: "var(--orange)" }}>
                                              📱 {group.phone}
                                            </a>
                                          ) : group.phone}
                                        </span>
                                      )}
                                      <div style={{ marginTop: "0.3rem", display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                                        {group.rows.map(r => (
                                          <span key={r.id} style={{
                                            background: "var(--beige)",
                                            borderRadius: "6px",
                                            padding: "0.15rem 0.5rem",
                                            fontSize: "0.82rem",
                                            color: "var(--text-main)",
                                          }}>
                                            {r.quantity}× {r.item}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.3rem", flexShrink: 0 }}>
                                      <StatusBadge status={group.status} />
                                      <PaymentBadge status={group.payment_status} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                <div style={cardStyle}>
                  <h2 style={{ ...sectionHeading }}>
                    📋 Orders
                    <span style={{ marginLeft: "0.75rem", fontSize: "0.85rem", color: "var(--text-muted)", fontWeight: "normal" }}>
                      {report.revenue.total_orders} orders · {report.revenue.total_items} items · {report.revenue.paid_orders}/{report.revenue.total_orders} paid
                    </span>
                  </h2>
                  <div style={{ overflowX: "auto" }}>
                    <table style={tableStyle}>
                      <thead>
                        <tr style={{ background: "var(--beige)", borderBottom: "2px solid var(--border)" }}>
                          <th style={thStyle}>Name</th>
                          <th style={thStyle}>Item</th>
                          <th style={thStyle}>Qty</th>
                          <th style={thStyle}>Location</th>
                          <th style={thStyle}>Amount</th>
                          <th style={thStyle}>Status</th>
                          <th style={thStyle}>Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.revenue.orders.map(o => {
                          const itemAmt = parseFloat(o.item_price || 0) * parseInt(o.quantity);
                          const delFee = parseFloat(o.delivery_fee || 0);
                          return (
                            <tr key={o.id} style={{ borderBottom: "1px solid var(--border)" }}>
                              <td style={tdStyle}>{o.name}</td>
                              <td style={tdStyle}>{o.item}</td>
                              <td style={tdStyle}>{o.quantity}</td>
                              <td style={tdStyle}>{o.location}</td>
                              <td style={tdStyle}>
                                {fmtRM(itemAmt + delFee)}
                                {delFee > 0 && (
                                  <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "block" }}>
                                    incl. {fmtRM(delFee)} delivery
                                  </span>
                                )}
                              </td>
                              <td style={tdStyle}><StatusBadge status={o.status} /></td>
                              <td style={tdStyle}><PaymentBadge status={o.payment_status} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div style={cardStyle}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                    <h2 style={{ ...sectionHeading, margin: 0 }}>💸 Other Expenses</h2>
                    <button onClick={() => setTab("expenses")} style={viewBtnStyle}>+ Add Expense</button>
                  </div>

                  {Object.keys(report.expenses.by_category).length > 0 && (
                    <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                      {Object.entries(report.expenses.by_category).map(([cat, amt]) => (
                        <div key={cat} style={{ background: "var(--beige)", borderRadius: "8px", padding: "0.5rem 0.85rem", fontSize: "0.85rem" }}>
                          <span style={{ color: "var(--text-muted)" }}>{CATEGORIES.find(c => c.value === cat)?.label || cat}:</span>
                          {" "}<strong>{fmtRM(amt)}</strong>
                        </div>
                      ))}
                    </div>
                  )}

                  {report.expenses.items.length === 0 ? (
                    <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>No expenses logged for this session.</p>
                  ) : (
                    <table style={tableStyle}>
                      <thead>
                        <tr style={{ background: "var(--beige)", borderBottom: "2px solid var(--border)" }}>
                          <th style={thStyle}>Description</th>
                          <th style={thStyle}>Category</th>
                          <th style={thStyle}>Amount</th>
                          <th style={thStyle}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.expenses.items.map(e => (
                          <tr key={e.id} style={{ borderBottom: "1px solid var(--border)" }}>
                            <td style={tdStyle}>{e.description}</td>
                            <td style={tdStyle}>{CATEGORIES.find(c => c.value === e.category)?.label || e.category}</td>
                            <td style={{ ...tdStyle, fontWeight: "bold", color: "var(--gold)" }}>{fmtRM(e.amount)}</td>
                            <td style={tdStyle}>
                              <button onClick={() => deleteExpense(e.id)} style={deleteBtnStyle}>🗑</button>
                            </td>
                          </tr>
                        ))}
                        <tr style={{ background: "var(--beige)" }}>
                          <td style={{ ...tdStyle, fontWeight: "bold" }} colSpan={2}>Total</td>
                          <td style={{ ...tdStyle, fontWeight: "bold", color: "var(--gold)" }}>{fmtRM(report.expenses.total)}</td>
                          <td />
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            );
          })()}

          {!selectedSession && !loading && (
            <div style={{ ...cardStyle, textAlign: "center", color: "var(--text-muted)" }}>
              <p>Select a session above to see the full profit breakdown.</p>
            </div>
          )}
        </>
      )}

      {tab === "loyalty" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem", gap: "1rem", flexWrap: "wrap" }}>
            <div>
              <h2 style={{ ...sectionHeading, marginBottom: "0.15rem" }}>🎁 Loyalty Offers</h2>
              <p style={{ margin: "0.3rem 0 0", fontSize: "0.85rem", color: "var(--text-muted)" }}>
                Create point-based offers and review your most loyal customers
              </p>
            </div>
            <button onClick={loadLoyaltyData} style={viewBtnStyle}>🔄 Refresh</button>
          </div>

          {loyaltyLoading && <p style={{ color: "var(--text-muted)" }}>Loading loyalty data...</p>}
          {loyaltyError && <p style={{ color: "var(--error)", fontSize: "0.9rem" }}>{loyaltyError}</p>}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
            <div style={cardStyle}>
              <h3 style={{ ...sectionHeading, marginBottom: "0.75rem" }}>{editingOfferId ? "✏️ Edit Offer" : "➕ New Offer"}</h3>
              <form onSubmit={handleOfferSubmit}>
                <label style={labelStyle}>Offer Name</label>
                <input
                  value={offerForm.name}
                  onChange={(e) => setOfferForm((prev) => ({ ...prev, name: e.target.value }))}
                  style={inputStyle}
                  placeholder="e.g. RM5 off at 100 points"
                />

                <label style={labelStyle}>Description</label>
                <textarea
                  value={offerForm.description}
                  onChange={(e) => setOfferForm((prev) => ({ ...prev, description: e.target.value }))}
                  style={{ ...inputStyle, minHeight: "90px", resize: "vertical" }}
                  placeholder="Explain what users get"
                />

                <label style={labelStyle}>Minimum Points</label>
                <input
                  type="number"
                  min="0"
                  value={offerForm.min_points}
                  onChange={(e) => setOfferForm((prev) => ({ ...prev, min_points: e.target.value }))}
                  style={{ ...inputStyle, maxWidth: "180px" }}
                />

                <label style={labelStyle}>Discount Amount (RM)</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={offerForm.discount_amount}
                  onChange={(e) => setOfferForm((prev) => ({ ...prev, discount_amount: e.target.value }))}
                  style={{ ...inputStyle, maxWidth: "180px" }}
                />

                <label style={{ ...labelStyle, marginBottom: "0.5rem" }}>
                  <input
                    type="checkbox"
                    checked={offerForm.active}
                    onChange={(e) => setOfferForm((prev) => ({ ...prev, active: e.target.checked }))}
                    style={{ marginRight: "0.5rem" }}
                  />
                  Active
                </label>

                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                  <button type="submit" style={createBtnStyle}>{editingOfferId ? "Save Offer" : "Create Offer"}</button>
                  {editingOfferId && (
                    <button type="button" onClick={resetOfferForm} style={viewBtnStyle}>Cancel Edit</button>
                  )}
                </div>

                {offerSuccess && <p style={{ ...successStyle, marginBottom: 0 }}>{offerSuccess}</p>}
              </form>
            </div>

            <div style={cardStyle}>
              <h3 style={{ ...sectionHeading, marginBottom: "0.75rem" }}>🏆 Top Customers</h3>
              {topCustomers.length === 0 ? (
                <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>No customer data yet.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr style={{ background: "var(--beige)", borderBottom: "2px solid var(--border)" }}>
                        <th style={thStyle}>Customer</th>
                        <th style={thStyle}>Points</th>
                        <th style={thStyle}>Orders</th>
                        <th style={thStyle}>Items</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCustomers.map((customer) => (
                        <tr key={customer.id} style={{ borderBottom: "1px solid var(--border)" }}>
                          <td style={tdStyle}>
                            <strong>{customer.name}</strong>
                            <span style={{ display: "block", color: "var(--text-muted)", fontSize: "0.75rem" }}>{customer.email}</span>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: "bold", color: "var(--success)" }}>{customer.loyalty_points || 0}</td>
                          <td style={tdStyle}>{customer.order_count || 0}</td>
                          <td style={tdStyle}>{customer.item_count || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={{ ...sectionHeading, marginBottom: "0.75rem" }}>Current Offers</h3>
            {loyaltyOffers.length === 0 ? (
              <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>No offers created yet.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr style={{ background: "var(--beige)", borderBottom: "2px solid var(--border)" }}>
                      <th style={thStyle}>Offer</th>
                      <th style={thStyle}>Min Points</th>
                      <th style={thStyle}>Discount</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loyaltyOffers.map((offer) => (
                      <tr key={offer.id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={tdStyle}>
                          <strong>{offer.name}</strong>
                          {offer.description && <span style={{ display: "block", color: "var(--text-muted)", fontSize: "0.75rem" }}>{offer.description}</span>}
                        </td>
                        <td style={tdStyle}>{offer.min_points || 0}</td>
                        <td style={{ ...tdStyle, fontWeight: "bold", color: "var(--success)" }}>RM {Number(offer.discount_amount || 0).toFixed(2)}</td>
                        <td style={tdStyle}>
                          <span style={{ background: offer.active ? "#EDF5EE" : "#FDECEC", color: offer.active ? "var(--success)" : "var(--error)", padding: "0.2rem 0.6rem", borderRadius: "999px", fontSize: "0.78rem", fontWeight: "bold" }}>
                            {offer.active ? "Active" : "Hidden"}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                            <button onClick={() => handleOfferEdit(offer)} style={viewBtnStyle}>Edit</button>
                            <button onClick={() => handleOfferToggle(offer)} style={viewBtnStyle}>{offer.active ? "Disable" : "Enable"}</button>
                            <button onClick={() => handleOfferDelete(offer.id)} style={deleteBtnStyle}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "feedback" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
            <div>
              <h2 style={{ ...sectionHeading, marginBottom: "0.15rem" }}>⭐ Customer Satisfaction</h2>
              <p style={{ margin: "0.3rem 0 0", fontSize: "0.85rem", color: "var(--text-muted)" }}>
                Pulled live from your Google Forms responses
              </p>
            </div>
            <button onClick={fetchFeedback} style={viewBtnStyle}>🔄 Refresh</button>
          </div>

          {feedbackLoading && <p style={{ color: "var(--text-muted)" }}>Loading feedback...</p>}
          {feedbackError && <p style={{ color: "var(--error)", fontSize: "0.9rem" }}>{feedbackError}</p>}

          {!feedbackLoading && feedback.length > 0 && (() => {
            const avg = (arr) => (arr.reduce((s, v) => s + v, 0) / arr.length).toFixed(1);
            const count = feedback.length;

            const avgOverall     = avg(feedback.map(f => f.overall));
            const avgTaste       = avg(feedback.map(f => f.taste));
            const avgPortion     = avg(feedback.map(f => f.portion));
            const avgValue       = avg(feedback.map(f => f.value));
            const avgConsistency = avg(feedback.map(f => f.consistency));

            const reorderCounts = feedback.reduce((acc, f) => {
              acc[f.reorder] = (acc[f.reorder] || 0) + 1;
              return acc;
            }, {});

            const complaintCounts = {};
            feedback.forEach(f => {
              f.disappointed.split(",").forEach(d => {
                const key = d.trim();
                if (key && key !== "Nothing, it was good") {
                  complaintCounts[key] = (complaintCounts[key] || 0) + 1;
                }
              });
            });
            const topComplaints = Object.entries(complaintCounts)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 4);

            const Stars = ({ val }) => {
              const filled = Math.round(parseFloat(val));
              return (
                <span style={{ fontSize: "1rem", letterSpacing: "0.1em" }}>
                  {[1,2,3,4,5].map(i => (
                    <span key={i} style={{ color: i <= filled ? "var(--gold)" : "var(--border)" }}>★</span>
                  ))}
                  <span style={{ marginLeft: "0.4rem", fontSize: "0.9rem", color: "var(--text-main)", fontWeight: "bold" }}>{val}</span>
                </span>
              );
            };

            return (
              <>
                <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
                  <StatCard label="Responses" value={count} color="var(--brown-mid)" />
                  <StatCard label="Overall Satisfaction" value={`${avgOverall} / 5`} color="var(--gold)" />
                  <StatCard label="Taste" value={`${avgTaste} / 5`} color="var(--success)" />
                  <StatCard label="Value for Money" value={`${avgValue} / 5`} color="var(--orange)" />
                </div>

                <div style={{ ...cardStyle, marginBottom: "1.5rem" }}>
                  <h3 style={{ ...sectionHeading, marginBottom: "0.75rem" }}>📊 Average Ratings</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    {[
                      { label: "Overall Satisfaction", val: avgOverall },
                      { label: "Taste",                 val: avgTaste },
                      { label: "Portion Size",          val: avgPortion },
                      { label: "Value for Money",       val: avgValue },
                      { label: "Consistency",           val: avgConsistency },
                    ].map(({ label, val }) => (
                      <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: "0.9rem", color: "var(--text-main)", minWidth: "180px" }}>{label}</span>
                        <div style={{ flex: 1, margin: "0 1rem" }}>
                          <div style={{ background: "var(--border)", borderRadius: "999px", height: "8px" }}>
                            <div style={{
                              width: `${(parseFloat(val) / 5) * 100}%`,
                              background: parseFloat(val) >= 4 ? "var(--success)" : parseFloat(val) >= 3 ? "var(--gold)" : "var(--error)",
                              borderRadius: "999px", height: "8px",
                            }} />
                          </div>
                        </div>
                        <Stars val={val} />
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
                  <div style={{ ...cardStyle, flex: 1, minWidth: "260px", marginBottom: 0 }}>
                    <h3 style={{ ...sectionHeading, marginBottom: "0.75rem" }}>⚠️ Top Complaints</h3>
                    {topComplaints.length === 0
                      ? <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>No complaints — all good! 🎉</p>
                      : topComplaints.map(([complaint, n]) => (
                        <div key={complaint} style={{ marginBottom: "0.6rem" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", marginBottom: "0.2rem" }}>
                            <span style={{ color: "var(--text-main)" }}>{complaint}</span>
                            <span style={{ color: "var(--text-muted)" }}>{n}x</span>
                          </div>
                          <div style={{ background: "var(--border)", borderRadius: "999px", height: "5px" }}>
                            <div style={{
                              width: `${(n / count) * 100}%`,
                              background: "var(--error)", borderRadius: "999px", height: "5px",
                            }} />
                          </div>
                        </div>
                      ))
                    }
                  </div>

                  <div style={{ ...cardStyle, flex: 1, minWidth: "220px", marginBottom: 0 }}>
                    <h3 style={{ ...sectionHeading, marginBottom: "0.75rem" }}>🔁 Would Order Again</h3>
                    {Object.entries(reorderCounts)
                      .sort((a, b) => b[1] - a[1])
                      .map(([label, n]) => (
                        <div key={label} style={{ marginBottom: "0.6rem" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", marginBottom: "0.2rem" }}>
                            <span style={{ color: "var(--text-main)" }}>{label}</span>
                            <span style={{ color: "var(--text-muted)" }}>{n} ({Math.round((n/count)*100)}%)</span>
                          </div>
                          <div style={{ background: "var(--border)", borderRadius: "999px", height: "5px" }}>
                            <div style={{
                              width: `${(n / count) * 100}%`,
                              background: label === "Definitely" ? "var(--success)" : label === "Probably" ? "var(--orange)" : "var(--gold)",
                              borderRadius: "999px", height: "5px",
                            }} />
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>

                <div style={cardStyle}>
                  <h3 style={{ ...sectionHeading, marginBottom: "0.75rem" }}>💬 What customers said</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", maxHeight: "400px", overflowY: "auto" }}>
                    {feedback
                      .filter(f => f.improve_one || f.comments)
                      .map((f, i) => (
                        <div key={i} style={{ background: "var(--beige)", borderRadius: "10px", padding: "0.75rem 1rem", borderLeft: "3px solid var(--orange)" }}>
                          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.35rem", flexWrap: "wrap" }}>
                            <span style={{ fontSize: "0.78rem", color: "var(--gold)" }}>
                              {"★".repeat(f.overall)}{"☆".repeat(5 - f.overall)}
                            </span>
                            <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{f.times_ordered}</span>
                            {f.reorder && (
                              <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>· {f.reorder} order again</span>
                            )}
                          </div>
                          {f.improve_one && (
                            <p style={{ margin: "0 0 0.25rem", fontSize: "0.85rem", color: "var(--text-main)" }}>
                              <strong>Improve:</strong> {f.improve_one}
                            </p>
                          )}
                          {f.comments && f.comments !== "." && f.comments !== ".." && (
                            <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                              "{f.comments}"
                            </p>
                          )}
                        </div>
                      ))
                    }
                  </div>
                </div>
              </>
            );
          })()}

          {!feedbackLoading && feedback.length === 0 && !feedbackError && (
            <div style={{ ...cardStyle, textAlign: "center", color: "var(--text-muted)" }}>
              <p>No feedback data yet. Click Refresh to load.</p>
            </div>
          )}
        </div>
      )}

      {tab === "expenses" && (
        <div style={cardStyle}>
          <h2 style={sectionHeading}>💸 Log an Expense</h2>
          <p style={{ color: "var(--text-muted)", fontSize: "0.88rem", marginTop: 0 }}>
            Use this for costs not in recipes — packaging, delivery bags, gas, etc.
          </p>

          <form onSubmit={addExpense}>
            <label style={labelStyle}>Session (optional)</label>
            <select value={selectedSession} onChange={e => setSelectedSession(e.target.value)} style={inputStyle}>
              <option value="">No specific session</option>
              {summary.map(s => (
                <option key={s.id} value={s.id}>{fmt(s.open_time)}</option>
              ))}
            </select>

            <label style={labelStyle}>Category</label>
            <select value={expCategory} onChange={e => setExpCategory(e.target.value)} style={inputStyle}>
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>

            <label style={labelStyle}>Description</label>
            <input
              value={expDesc}
              onChange={e => setExpDesc(e.target.value)}
              style={inputStyle}
            />

            <label style={labelStyle}>Amount (RM)</label>
            <input
              type="number" min="0.01" step="0.01"
              value={expAmount}
              onChange={e => setExpAmount(e.target.value)}
              style={{ ...inputStyle, maxWidth: "160px" }}
            />

            {expError && <p style={errorStyle}>{expError}</p>}
            {expSuccess && <p style={successStyle}>{expSuccess}</p>}

            <button type="submit" style={createBtnStyle}>+ Add Expense</button>
          </form>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, note }) {
  return (
    <div style={{
      background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "14px",
      padding: "1rem 1.25rem", borderTop: `4px solid ${color}`,
      minWidth: "140px", flex: 1, boxShadow: "0 4px 24px rgba(92, 51, 23, 0.08)",
    }}>
      <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--brown-mid)", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.04em", fontFamily: "'Lato', sans-serif" }}>{label}</p>
      <p style={{ margin: "0.4rem 0 0", fontSize: "1.2rem", fontWeight: "bold", color: "var(--text-main)", fontFamily: "'Playfair Display', Georgia, serif" }}>{value}</p>
      {note && <p style={{ margin: "0.25rem 0 0", fontSize: "0.72rem", color: "var(--text-muted)" }}>{note}</p>}
    </div>
  );
}

function CalcRow({ label, value, color, sign }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "0.92rem" }}>
      <span style={{ color: "var(--text-main)" }}>
        {sign && <span style={{ color, fontWeight: "bold", marginRight: "0.4rem" }}>{sign}</span>}
        {label}
      </span>
      <span style={{ color, fontWeight: "bold" }}>{value}</span>
    </div>
  );
}

function MarginBadge({ margin }) {
  const m = parseFloat(margin);
  const s = m >= 30 ? { bg: "#EDF5EE", color: "var(--success)" }
    : m >= 10 ? { bg: "#FEF4E4", color: "var(--gold)" }
    : { bg: "#FDECEC", color: "var(--error)" };
  return (
    <span style={{ background: s.bg, color: s.color, padding: "0.2rem 0.6rem", borderRadius: "999px", fontSize: "0.8rem", fontWeight: "bold" }}>
      {m}%
    </span>
  );
}

function Dash() {
  return <span style={{ color: "var(--border)" }}>—</span>;
}

function StatusBadge({ status }) {
  const s = { Pending: { bg: "#FEF4E4", color: "var(--orange)" }, Ready: { bg: "#EDF5EE", color: "var(--success)" }, Completed: { bg: "var(--beige)", color: "var(--text-muted)" } }[status] || { bg: "#FEF4E4", color: "var(--orange)" };
  return <span style={{ background: s.bg, color: s.color, padding: "0.2rem 0.6rem", borderRadius: "999px", fontSize: "0.78rem", fontWeight: "bold" }}>{status}</span>;
}

function PaymentBadge({ status }) {
  const s = { unpaid: { bg: "#FEF4E4", color: "var(--orange)", label: "Unpaid" }, submitted: { bg: "#FEF4E4", color: "var(--gold)", label: "Submitted" }, confirmed: { bg: "#EDF5EE", color: "var(--success)", label: "✅ Paid" }, rejected: { bg: "#FDECEC", color: "var(--error)", label: "Rejected" } }[status || "unpaid"];
  return <span style={{ background: s.bg, color: s.color, padding: "0.2rem 0.6rem", borderRadius: "999px", fontSize: "0.78rem", fontWeight: "bold" }}>{s.label}</span>;
}

const sectionHeading = { marginTop: 0, fontSize: "1.1rem", fontFamily: "'Playfair Display', Georgia, serif", color: "var(--text-main)" };
const cardStyle = { background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "16px", padding: "1.5rem", boxShadow: "0 4px 24px rgba(92, 51, 23, 0.08)", marginBottom: "1.5rem" };
const tableStyle = { width: "100%", borderCollapse: "collapse" };
const labelStyle = { display: "block", fontWeight: "600", fontSize: "0.85rem", color: "var(--brown-mid)", marginBottom: "0.35rem", textTransform: "uppercase", letterSpacing: "0.04em" };
const inputStyle = { display: "block", width: "100%", padding: "0.55rem 0.7rem", marginBottom: "1rem", borderRadius: "10px", border: "1.5px solid var(--border)", background: "var(--beige)", fontSize: "0.95rem", boxSizing: "border-box", color: "var(--text-main)" };
const thStyle = { padding: "0.65rem 1rem", textAlign: "left", fontSize: "0.78rem", fontWeight: "700", color: "var(--brown-mid)", textTransform: "uppercase", letterSpacing: "0.04em" };
const tdStyle = { padding: "0.65rem 1rem", fontSize: "0.88rem", color: "var(--text-main)", verticalAlign: "middle" };
const tabStyle = { background: "var(--warm-white)", border: "1px solid var(--border)", borderRadius: "10px", padding: "0.5rem 1.25rem", fontSize: "0.95rem", cursor: "pointer", color: "var(--text-muted)" };
const activeTabStyle = { ...tabStyle, background: "var(--orange)", color: "white", border: "1px solid var(--orange)" };
const noteBoxStyle = { background: "#FEF4E4", border: "1px solid var(--orange)", borderRadius: "10px", padding: "0.65rem 1rem", fontSize: "0.85rem", color: "var(--orange)", marginBottom: "1rem" };
const createBtnStyle = { background: "var(--orange)", color: "white", border: "none", padding: "0.65rem 1.5rem", borderRadius: "10px", fontSize: "1rem", fontWeight: "700", cursor: "pointer", boxShadow: "0 4px 14px rgba(224, 123, 57, 0.35)" };
const exportBtnStyle = { background: "var(--success)", color: "white", border: "none", padding: "0.55rem 1.25rem", borderRadius: "10px", fontSize: "0.9rem", cursor: "pointer", fontWeight: "700" };
const viewBtnStyle = { background: "var(--beige)", border: "1px solid var(--border)", borderRadius: "8px", padding: "0.3rem 0.75rem", fontSize: "0.85rem", cursor: "pointer", color: "var(--text-main)" };
const deleteBtnStyle = { background: "#FDECEC", border: "1px solid #E8A8A8", borderRadius: "8px", padding: "0.3rem 0.6rem", fontSize: "0.82rem", cursor: "pointer", color: "var(--error)" };
const errorStyle = { color: "var(--error)", fontSize: "0.88rem", margin: "0.5rem 0" };
const successStyle = { color: "var(--success)", fontSize: "0.88rem", margin: "0.5rem 0" };

export default Reports;